import { PAGE } from '@/routes';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import infoIcon from '@/assets/icons/info.svg';
import percentageIcon from '@/assets/icons/percentage.svg';
import euroIcon from '@/assets/icons/euro.svg';
import invoiceIcon from '@/assets/icons/invoice.svg';
import invoiceWhiteIcon from '@/assets/icons/invoice-white.svg';
import workActivityIcon from '@/assets/icons/work-activity-primary.svg';
import workActivityWhiteIcon from '@/assets/icons/work-activity-white.svg';
import noInvoiceIcon from '@/assets/icons/no-invoice.svg';
import noWorkActivityIcon from '@/assets/icons/no-work-activity.svg';
import vaultIcon from '@/assets/icons/vault.svg';
import { Button } from '@/components/ui/button';
import { bankAccountService } from '@/services/bank-accounts.service';
import { BankAccount } from '@/services/types/bank-accounts.types';
import ExplanatoryModal from '@/components/molecules/ExplanatoryModal';
import MobileAppBar from '@/components/molecules/MobileAppBar';

import baExpanatoryImg from '@/assets/images/ba-explanatory-1.png';
import baExpanatoryImg2 from '@/assets/images/ba-explanatory-2.png';
import { Invoice } from '@/services/types/invoice.types';
import { invoiceService } from '@/services/invoice.service';
import { workActivityService } from '@/services/work-activity.service';
import InvoicesTable from '@/components/molecules/invoice/InvoicesTable';
import WorkActivityTable from '@/components/molecules/workActivity/WorkActivityTable';
import TabPills from '@/components/molecules/TabPills';
import { WorkActivity } from '@/services/types/work-activity.types';
import { useBudgetPermissions } from '@/hooks/useBudgetPermissions';

type ContentTab = 'invoice' | 'work-activity';

const steps = [
  {
    title: 'What Is a Bank Account?',
    message:
      'Bank accounts act as the primary source for payments and deposits, seamlessly integrating with your budget.',
    img: baExpanatoryImg
  },
  {
    title: 'Tracking Invoices in Your Bank Account',
    message:
      'All invoices are linked to bank accounts, enabling tracking of cash inflows and outflows while maintaining account balances.',
    img: baExpanatoryImg2
  }
];

const BankAccountsPage = () => {
  const [bankAccountMode, setBankAccountMode] = useState('percentage');
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [isLoadingBankAccounts, setIsLoadingBankAccounts] = useState(false);
  const [totalInitialAmount, setTotalInitialAmount] = useState(0);
  const [totalRemainingBalance, setTotalRemainingBalance] = useState(0);
  const [percentageRemaining, setPercentageRemaining] = useState(0);
  const [isExplanatoryOpen, setIsExplanatoryOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [activeInvoiceTab, setActiveInvoiceTab] = useState('All');
  const [activeContentTab, setActiveContentTab] = useState<ContentTab>('invoice');
  const [workActivities, setWorkActivities] = useState<WorkActivity[]>([]);
  const [loadingWorkActivities, setLoadingWorkActivities] = useState(false);
  const [totalWorkActivityCount, setTotalWorkActivityCount] = useState(0);

  const { budgetId = '' } = useParams();

  const navigate = useNavigate();
  const { can } = useBudgetPermissions({ budgetId });
  const canCreateBankAccount = can('bankAccount:create');
  const canCreateInvoice = can('invoice:create');

  const handleInvoiceSelect = (invoice: Invoice & { budget?: any }) => {
    if (budgetId) {
      navigate(`${PAGE.BUDGETS.path}/${budgetId}/invoices/${invoice._id}`, {
        state: { from: 'bankAccount' }
      });
    }
  };

  useEffect(() => {
    fetchBankAccounts();
  }, [budgetId]);

  useEffect(() => {
    // Calculate sums and percentages when bank accounts data changes
    if (bankAccounts && bankAccounts.length > 0) {
      const totalInitial = bankAccounts.reduce((sum, account) => sum + (account.initialAmount || 0), 0);
      const totalRemaining = bankAccounts.reduce((sum, account) => sum + (account.remainingBalance || 0), 0);

      setTotalInitialAmount(totalInitial);
      setTotalRemainingBalance(totalRemaining);

      // Calculate percentage remaining - handle division by zero
      const percentage = totalInitial > 0 ? (totalRemaining / totalInitial) * 100 : 0;
      setPercentageRemaining(parseFloat(percentage.toFixed(2)));
    }
  }, [bankAccounts]);

  useEffect(() => {
    fetchBudgetInvoices();
  }, [activeInvoiceTab, budgetId]);

  useEffect(() => {
    fetchBudgetWorkActivities();
  }, [budgetId]);

  const fetchBudgetWorkActivities = async () => {
    if (!budgetId) return;
    try {
      setLoadingWorkActivities(true);
      const response = await workActivityService.getWorkActivities(budgetId, {
        type: 'all',
        page: 1,
        limit: 8
      });
      setWorkActivities(Array.isArray(response.data) ? response.data : []);
      setTotalWorkActivityCount(response.totalCount ?? 0);
    } catch (error) {
      console.error('Error fetching budget work activities:', error);
      setWorkActivities([]);
      setTotalWorkActivityCount(0);
    } finally {
      setLoadingWorkActivities(false);
    }
  };

  const fetchBudgetInvoices = async () => {
    if (budgetId) {
      try {
        const { data: invoicesData } = await invoiceService.getInvoices({
          page: 1,
          limit: 12,
          budget_id: budgetId,
          invoice_category: activeInvoiceTab === 'All' ? undefined : activeInvoiceTab.toUpperCase(),
          type: undefined
        });
        const filteredInvoices =
          invoicesData.data && invoicesData.data.length > 0
            ? invoicesData.data.filter((invoice: any) => invoice.is_draft === false)
            : [];
        setInvoices(filteredInvoices || []);
      } catch (error) {
        console.error('Error fetching budget invoices:', error);
      } finally {
        setLoadingInvoices(false);
      }
    }
  };

  const fetchBankAccounts = async () => {
    setIsLoadingBankAccounts(true);
    try {
      const response = await bankAccountService.getBankAccounts(budgetId);
      setBankAccounts(response?.data);
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
    } finally {
      setIsLoadingBankAccounts(false);
    }
  };

  const handleBankAccountClick = (bankAccountId: string) => {
    navigate(PAGE?.BANK_ACCOUNTS?.children?.DETAIL.replace(':bankAccountId', bankAccountId));
  };

  // Calculate percentage change from initial amount
  const calculatePercentageChange = () => {
    if (totalInitialAmount === 0) return 0;
    const change = ((totalRemainingBalance - totalInitialAmount) / totalInitialAmount) * 100;
    return parseFloat(change.toFixed(2));
  };

  // Format currency as euros
  const formatEuro = (amount: any) => {
    return `€${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleAddClick = () => {
    navigate(`/budgets/${budgetId}/bank-accounts/add`);
  };

  return (
    <>
      <MobileAppBar
        title="Bank Account Overview"
        rightIcon={infoIcon}
        onRightIconClick={() => {
          setCurrentStep(0);
          setIsExplanatoryOpen(true);
        }}
        rightIconClassName="w-[24px] h-[24px]"
      />
      <div className="h-full w-full min-w-0 flex flex-col items-stretch gap-4 p-4 md:p-0 relative">
        {/* Centered BANK ACCOUNT header - mobile only */}
        <h1 className="md:hidden text-base font-bold uppercase text-center">BANK ACCOUNT</h1>

        <div className="w-full min-w-0 flex flex-col md:flex-row items-stretch justify-between gap-4 h-full">
          <div className="flex flex-col items-stretch flex-1 min-w-0 gap-5">
            {/* Balance card */}
            <div className="border bg-white rounded-[16px] p-4">
              <header className="hidden md:flex items-center justify-between mb-2">
                <h2 className="text-2xl font-semibold">Bank Account</h2>
                <button
                  title="info"
                  onClick={() => {
                    setCurrentStep(0);
                    setIsExplanatoryOpen(true);
                  }}
                >
                  <img src={infoIcon} alt="info-icon" />
                </button>
              </header>
              <div className="flex items-start justify-between mt-0 md:mt-5">
                <div>
                  <p className="text-primary font-medium text-sm md:text-base md:font-bold leading-[180%]">Balance</p>
                  {bankAccountMode === 'percentage' ? (
                    <p className="text-primary text-[1.25rem] md:text-[1.75rem] font-bold leading-[135%] md:leading-[100%] mb-[0.5rem]">
                      {percentageRemaining}%
                    </p>
                  ) : (
                    <p className="text-primary text-[1.25rem] md:text-[1.75rem] font-bold leading-[135%] md:leading-[100%] mb-[0.5rem]">
                      {formatEuro(totalRemainingBalance)}
                    </p>
                  )}
                  <span className="text-secondary text-xs md:text-sm font-medium">
                    {calculatePercentageChange() >= 0 ? '+' : ''}
                    {calculatePercentageChange()}% from Initial amount
                  </span>
                </div>
                <div>
                  <Tabs defaultValue="percentage" value={bankAccountMode} onValueChange={setBankAccountMode}>
                    <TabsList className="bg-lightPurple-200 h-auto py-[0.2rem]">
                      <TabsTrigger value="percentage" className="group md:py-[0.3rem]">
                        <img
                          src={percentageIcon}
                          alt="percentage-icon"
                          width={13}
                          height={17}
                          className="group-data-[state=active]:brightness-[100]"
                        />
                      </TabsTrigger>
                      <TabsTrigger value="euro" className="group md:py-[0.3rem]">
                        <img
                          src={euroIcon}
                          alt="euro-icon"
                          width={12}
                          height={12}
                          className="w-[12px] h-[12px] group-data-[state=active]:brightness-[100]"
                        />
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
              </div>
            </div>

            {/* All Bank Accounts - mobile only (list from sidebar) */}
            <div className="md:hidden">
              <p className="font-bold mb-4">All Bank Account</p>
              {isLoadingBankAccounts ? (
                <p className="text-sm text-secondary text-center py-4">Loading...</p>
              ) : (
                <div className="flex flex-col gap-4">
                  {bankAccounts && bankAccounts.length > 0 ? (
                    bankAccounts.map((bankAccount: BankAccount & { _id?: string }) => (
                      <button
                        onClick={() => handleBankAccountClick(bankAccount._id!)}
                        key={bankAccount._id}
                        className="flex items-center bg-white md:bg-lightPurple border border-transparent rounded-[16px] min-h-[88px] hover:border-lightPurple-300 transition-all duration-300"
                      >
                        <div className="px-5">
                          <img className="w-[40px] h-[40px]" src={vaultIcon} alt="vault-icon" />
                        </div>
                        <div className="border-l border-lightPurple-200 p-6 py-4 text-left flex-1">
                          <p className="font-bold leading-[110%] capitalize">{bankAccount.accountName || '-'}</p>
                          <span className="text-xs text-secondary leading-[100%]">
                            Remaining Balance €{bankAccount?.remainingBalance?.toLocaleString() || '-'}
                          </span>
                        </div>
                      </button>
                    ))
                  ) : (
                    <p className="text-sm text-secondary text-center py-4">No bank accounts found</p>
                  )}
                </div>
              )}
            </div>

            {/* Invoice / Work Activity section */}
            <section className="mt-7 md:mt-0 md:flex-1 min-w-0 md:bg-white md:p-4 md:rounded-[16px] md:border md:border-lightGray md:flex md:flex-col">
              <header className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <Tabs
                  value={activeContentTab}
                  onValueChange={(value) => setActiveContentTab(value as ContentTab)}
                  className="w-full md:w-auto"
                >
                  <TabsList className="bg-lightPurple-200 h-auto py-[0.2rem] w-full grid grid-cols-2 md:inline-flex md:w-auto">
                    <TabsTrigger value="invoice" className="group py-[0.3rem] px-3 w-full">
                      <h4 className="flex items-center justify-center md:justify-start gap-2.5">
                        <img src={invoiceIcon} alt="invoice-icon" className="group-data-[state=active]:hidden" />
                        <img
                          src={invoiceWhiteIcon}
                          alt="invoice-icon"
                          className="hidden group-data-[state=active]:block"
                        />
                        <span className="font-bold text-primary group-data-[state=active]:text-white">
                          Recent Invoice
                        </span>
                      </h4>
                    </TabsTrigger>
                    <TabsTrigger value="work-activity" className="group py-[0.3rem] px-3 w-full">
                      <h4 className="flex items-center justify-center md:justify-start gap-2.5">
                        <img
                          src={workActivityIcon}
                          alt="work-activity-icon"
                          className="group-data-[state=active]:hidden"
                        />
                        <img
                          src={workActivityWhiteIcon}
                          alt="work-activity-icon"
                          className="hidden group-data-[state=active]:block"
                        />
                        <span className="font-bold text-primary group-data-[state=active]:text-white">
                          Work Activity
                        </span>
                      </h4>
                    </TabsTrigger>
                  </TabsList>
                </Tabs>

                {activeContentTab === 'invoice' && invoices.length > 0 && (
                  <Button
                    variant="text"
                    onClick={() => navigate(`/budgets/${budgetId}/invoices`)}
                    className="hidden md:inline-flex py-0 h-[30px] shrink-0"
                  >
                    See All
                  </Button>
                )}
                {activeContentTab === 'work-activity' && totalWorkActivityCount > 7 && (
                  <Button
                    variant="text"
                    onClick={() => navigate(`${PAGE.BUDGETS.path}/${budgetId}/work-activity`)}
                    className="hidden md:inline-flex py-0 h-[30px] shrink-0"
                  >
                    See All
                  </Button>
                )}
              </header>

              {activeContentTab === 'invoice' &&
                (invoices.length > 0 || activeInvoiceTab !== 'All' ? (
                  <div className="flex flex-col gap-2 min-w-0 bg-lightPurple rounded-[16px] p-4 py-5 mt-4 md:mt-3 md:p-0 md:bg-white">
                    <TabPills
                      options={['All', 'Expense', 'Revenue']}
                      value={activeInvoiceTab}
                      onValueChange={setActiveInvoiceTab}
                    />

                    <InvoicesTable
                      invoices={invoices.slice(0, 10)}
                      setSelectedInvoice={() => {}}
                      onInvoiceSelect={handleInvoiceSelect}
                    />
                  </div>
                ) : (
                  <div className="bg-lightPurple rounded-[16px] p-6 flex items-center justify-center flex-col gap-3 mt-4 md:bg-white md:flex-1">
                    <div className="flex flex-col items-center justify-center md:flex-row md:gap-4">
                      <img src={noInvoiceIcon} alt="no invoice icon" className="md:w-[40px] md:h-[40px]" />
                      <span className="mt-[3px] text-xs font-medium text-center leading-[16.5px] md:text-left">
                        <span className="md:text-base md:font-medium md:block mb-[-3px] md:text-foreground">
                          There are no Invoice to show.
                        </span>{' '}
                        <span className="md:text-secondary md:text-base md:font-medium">
                          Please create a new invoice to allocate budget!
                        </span>
                      </span>
                    </div>
                    {canCreateInvoice && (
                      <Button
                        onClick={() =>
                          navigate(`${PAGE.INVOICE.path}/${PAGE.INVOICE.children.CREATE}`, {
                            state: { from: 'budget', budgetId }
                          })
                        }
                        variant="primary-reverse"
                        className="rounded-full font-semibold px-5 text-base"
                      >
                        Create an Invoice
                      </Button>
                    )}
                  </div>
                ))}

              {activeContentTab === 'work-activity' &&
                (workActivities.length > 0 || loadingWorkActivities ? (
                  <div className="flex flex-col gap-2 min-w-0 bg-lightPurple rounded-[16px] p-4 py-5 mt-4 md:mt-3 md:p-0 md:bg-white">
                    <WorkActivityTable
                      workActivities={workActivities.slice(0, 7)}
                      loading={loadingWorkActivities}
                      budgetId={budgetId}
                      onWorkActivitySelect={(wa) => {
                        if (budgetId) {
                          navigate(`${PAGE.BUDGETS.path}/${budgetId}/work-activity/${wa._id}`);
                        }
                      }}
                    />
                  </div>
                ) : (
                  <div className="bg-lightPurple rounded-[16px] p-6 flex items-center justify-center flex-col gap-3 mt-4 md:bg-white md:flex-1">
                    <div className="flex flex-col items-center justify-center md:flex-row md:gap-4">
                      <img src={noWorkActivityIcon} alt="no work activity icon" className="md:w-[40px] md:h-[40px]" />
                      <span className="mt-[3px] text-xs font-medium text-center leading-[16.5px] md:text-left">
                        <span className="md:text-base md:font-medium md:block mb-[-3px] md:text-foreground">
                          There are no work activity to show.
                        </span>{' '}
                        <span className="md:text-secondary md:text-base md:font-medium">
                          You can create work activity to request a payment
                        </span>
                      </span>
                    </div>
                    <Button
                      onClick={() =>
                        navigate(`${PAGE.BUDGETS.path}/${budgetId}/work-activity/create`, {
                          state: { from: 'budget', budgetId }
                        })
                      }
                      variant="primary-reverse"
                      className="rounded-full font-semibold px-5 text-base"
                    >
                      Create a Work Activity
                    </Button>
                  </div>
                ))}
            </section>
          </div>

          {/* Sidebar - desktop only */}
          <aside className="hidden md:block w-full max-w-[360px] shrink-0 md:w-[360px] border bg-white rounded-[16px] p-4">
            <p className="font-bold mb-4">All Bank Accounts</p>
            {isLoadingBankAccounts ? (
              <p className="text-sm text-secondary text-center py-4">Loading...</p>
            ) : (
              <div className="flex flex-col gap-4">
                {bankAccounts && bankAccounts?.length > 0 ? (
                  bankAccounts.map((bankAccount: BankAccount & { _id?: string }) => (
                    <button
                      onClick={() => handleBankAccountClick(bankAccount._id!)}
                      key={bankAccount._id}
                      className="flex items-center border rounded-[8px] min-h-[88px] hover:border-lightPurple-300 transition-all duration-300"
                    >
                      <div className="px-5">
                        <img className="w-[40px] h-[40px]" src={vaultIcon} alt="vault-icon" />
                      </div>
                      <div className="border-l p-6 py-4 text-left">
                        <p className="font-bold leading-[110%] capitalize">{bankAccount.accountName || '-'}</p>
                        <span className="text-xs text-secondary leading-[100%]">
                          Remaining Balance €{bankAccount?.remainingBalance?.toLocaleString() || '-'}
                        </span>
                      </div>
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-secondary text-center py-4">No bank accounts found</p>
                )}
              </div>
            )}
          </aside>
        </div>

        {/* FAB - Add Bank Account */}
        {canCreateBankAccount && (
          <div className="rounded-[16px] md:rounded-[22px] flex-1 flex items-center justify-center fixed md:absolute bottom-[26px] right-[26px] md:right-[36px] shadow-[0px_4px_4px_0px_#00000040]">
            <Button
              onClick={handleAddClick}
              className="px-3 w-[48px] h-[48px] md:w-[64px] md:h-[64px] rounded-[16px] md:rounded-[22px]"
            >
              <span className="text-white text-[2.5rem] font-light">+</span>
            </Button>
          </div>
        )}
      </div>

      {/* Explanatory Modal */}
      {isExplanatoryOpen && (
        <ExplanatoryModal
          isOpen={isExplanatoryOpen}
          steps={steps}
          currentStep={currentStep}
          buttonText="Next"
          onButtonClick={() => {
            setCurrentStep(currentStep + 1);
          }}
          onClose={() => {
            setIsExplanatoryOpen(false);
          }}
          onStepChange={setCurrentStep}
        />
      )}
    </>
  );
};

export default BankAccountsPage;
